import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CashoutBreakdown } from './CashoutBreakdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cashouts on a rollback verification hold: staff re-check payments, type the net still owed, then release.
export const HeldCashoutsPanel = ({ onReleased }) => {
    const [holds, setHolds] = useState([]);
    const [typed, setTyped] = useState({});
    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/admin/cashout-holds`, { withCredentials: true });
            setHolds(data.holds || []);
        } catch { setHolds([]); }
    }, []);
    useEffect(() => { load(); }, [load]);
    const verify = async (h) => {
        try {
            await axios.post(`${API}/admin/cashout-holds/verify`, { transaction_id: h.id, confirmed_net_still_owed: Number(typed[h.id]) }, { withCredentials: true });
            toast.success(`Released ${h.user_name || 'cashout'} — back in the payout queue`);
            await load();
            onReleased?.();
        } catch (e) { toast.error(e.response?.data?.detail || 'Could not verify'); }
    };
    if (!holds.length) return null;
    return (
        <div className="rounded-lg border border-amber-500/50 bg-amber-500/5 p-3 mb-3 space-y-2" data-testid="held-cashouts-panel">
            <div className="flex items-center gap-2 text-amber-300 text-xs font-bold uppercase tracking-widest">
                <ShieldAlert className="w-4 h-4" /> {holds.length} cashout(s) on verification hold — check payments, then type the net still owed
            </div>
            {holds.map((h) => (
                <div key={h.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-white/5 pt-2" data-testid={`held-cashout-${h.id}`}>
                    <div className="text-sm text-white">{h.user_name || h.user_id} <span className="text-zinc-500 text-xs">via {h.payout_method || '—'}</span>
                        <CashoutBreakdown b={{ ...h.breakdown, still_owed: undefined }} testId={`held-breakdown-${h.id}`} />
                    </div>
                    <div className="flex items-center gap-2">
                        <Input className="h-8 w-28 bg-black/40" type="number" step="0.01" placeholder="net owed $"
                            value={typed[h.id] ?? ''} onChange={(e) => setTyped({ ...typed, [h.id]: e.target.value })} data-testid={`held-input-${h.id}`} />
                        <Button size="sm" disabled={!typed[h.id]} onClick={() => verify(h)} data-testid={`held-verify-${h.id}`}>Verify & release</Button>
                    </div>
                </div>
            ))}
        </div>
    );
};
